import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Feature {
  menu_id?: string;
  title: string;
  icon?: string | null;
  code: string;
  is_menu?: boolean;
  category?: string;
  ordering: number;
  is_enabled: boolean;
  is_editable?: boolean;
  parent_id?: string | null;
  subMenus: Feature[];
}

interface FlatFeature {
  code: string;
  is_enabled: boolean;
  is_editable: boolean;
  is_menu: boolean;
  parent_id?: string;
}

export interface FeaturePermissionContextValue {
  isEnabled: (code: string) => boolean;
  isEditable: (code: string) => boolean;
  isMenuEnabled: (code: string) => boolean;
  getScreenActions: (parentCode: string) => FlatFeature[];
  getNavItems: () => FlatFeature[];
  loadFeatures: (features: Feature[]) => void;
  clearFeatures: () => void;
  getFeature: (code: string) => FlatFeature | undefined;
}

// ─── Storage key ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'outlet_menus';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function saveToStorage(features: Feature[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(features));
  } catch (e) {
    console.warn('[FeaturePermission] Could not save to sessionStorage:', e);
  }
}

function loadFromStorage(): Feature[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Feature[]) : [];
  } catch (e) {
    console.warn('[FeaturePermission] Could not read from sessionStorage:', e);
    return [];
  }
}

function clearStorage(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

const MENU_CODES = new Set([
  // POS
  'POS',
  'POS_MENU_MANAGEMENT',
  'POS_OFFER_COMBOS',
  'POS_TABLE_MANAGEMENT',
  'POS_HOSTESS_VIEW',

  // INVENTORY
  'INVENTORY',
  'INVENTORY_DASHBOARD',
  'INVENTORY_SUPPLY_CHAIN_MANAGEMENT',
  'INVENTORY_SUPPLY_CHAIN_DASHBOARD',
  'INVENTORY_INVENTORY_OVERVIEW',
  'INVENTORY_PURCHASES',
  'INVENTORY_INVOICING',
  'INVENTORY_SUPPLIER_MANAGEMENT',
  'INVENTORY_AUTO_ORDERING',
  'INVENTORY_STOCK_REAL_TIME',
  'INVENTORY_BRANCH_TRANSFER',
  'INVENTORY_WASTE_CANCELLATION_MANAGEMENT',
  'INVENTORY_SKU',
  'INVENTORY_RECIPES',

  // STAFFING
  'STAFFING',
  'STAFFING_EMPLOYEE_DIRECTORY',
  'STAFFING_EMPLOYEE_ATTENDANCE',
  'STAFFING_SHIFT_PLANNING',
  'STAFFING_STAFF_SETTINGS',

  // CRM
  'CRM',
  'CRM_DASHBOARD',
  'CRM_CUSTOMER_SEGMENTS_PROFILES',
  'CRM_MEMBERS_INFO',
  'CRM_CAMPAIGN_CONTROL',
  'CRM_LOYALTY_REWARDS',
  'CRM_FEEDBACK_RATINGS',

  // FINANCE
  'FINANCE',
  'FINANCE_DASHBOARD',
  'FINANCE_PROFIT_ANALYTICS',
  'FINANCE_INVOICE_COGS_REPORT',
  'FINANCE_SALES_REPORT',
  'FINANCE_TAXATION_COMPLIANCE',
  'FINANCE_TRANSACTIONS',

  // RESTAURANT ADMIN
  'RESTAURANT_ADMIN',
  'RESTAURANT_ADMIN_DASHBOARD',
  'RESTAURANT_ADMIN_POS',
  'RESTAURANT_ADMIN_PAYMENT_SETTINGS',
  'RESTAURANT_ADMIN_FINANCE_ANALYSIS',
  'RESTAURANT_ADMIN_SETTINGS',
  'RESTAURANT_ADMIN_RBAC',
  'RESTAURANT_ADMIN_MULTI_OUTLETS',
  'RESTAURANT_ADMIN_KDSKOT',
  'RESTAURANT_ADMIN_AUDIT_LOGS',
  'RESTAURANT_ADMIN_FX_SUPPORT',
  'RESTAURANT_ADMIN_INTEGRATION',
  'RESTAURANT_ADMIN_MANAGE_NEWS_FEED'
]);

function flattenFeatures(
  features: Feature[],
  map: Map<string, FlatFeature>,
  parentChildMap: Map<string, string[]>,
  parentCode?: string,
): void {
  for (const f of features) {
    const isMenu = typeof f.is_menu === 'boolean' ? f.is_menu : MENU_CODES.has(f.code);
    const isEditable = typeof f.is_editable === 'boolean' ? f.is_editable : true;
    map.set(f.code, {
      code: f.code,
      is_enabled: f.is_enabled,
      is_editable: isEditable,
      is_menu: isMenu,
      parent_id: f.parent_id || parentCode || undefined,
    });

    if (parentCode) {
      const siblings = parentChildMap.get(parentCode) ?? [];
      siblings.push(f.code);
      parentChildMap.set(parentCode, siblings);
    }

    if (f.subMenus?.length) {
      flattenFeatures(f.subMenus, map, parentChildMap, f.code);
    }
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const FeaturePermissionContext =
  createContext<FeaturePermissionContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function FeaturePermissionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const mapRef = useRef(new Map<string, FlatFeature>());
  const parentChildRef = useRef(new Map<string, string[]>());
  const [, forceUpdate] = useState(0);

  // ── Rehydrate on mount ──────────────────────────────────────────────────
  // On every page load/refresh, check sessionStorage first.
  // If menus are saved there, rebuild the map immediately —
  // no API call needed, no flicker, no empty permissions.
  if (mapRef.current.size === 0) {
    const stored = loadFromStorage();
    if (stored.length > 0) {
      flattenFeatures(stored, mapRef.current, parentChildRef.current);
    }
  }

  // ── loadFeatures ────────────────────────────────────────────────────────
  // Called on login OR outlet switch.
  // Saves raw features to sessionStorage, rebuilds the in-memory map.
  const loadFeatures = useCallback((features: Feature[]) => {
    // 1. Persist raw JSON to sessionStorage (survives refresh)
    saveToStorage(features);
    // 2. Rebuild in-memory map
    mapRef.current.clear();
    parentChildRef.current.clear();
    flattenFeatures(features, mapRef.current, parentChildRef.current);

    // 3. Trigger re-render of all consumers
    forceUpdate(n => n + 1);
  }, []);

  // ── clearFeatures ───────────────────────────────────────────────────────
  // Call this on logout — wipes both storage and the in-memory map.
  const clearFeatures = useCallback(() => {
    clearStorage();
    mapRef.current.clear();
    parentChildRef.current.clear();
    forceUpdate(n => n + 1);
  }, []);

  const isEnabled = useCallback(
    (code: string) => mapRef.current.get(code)?.is_enabled ?? false,
    [],
  );

  const isEditable = useCallback(
    (code: string) => mapRef.current.get(code)?.is_editable ?? false,
    [],
  );

  const isMenuEnabled = useCallback((code: string) => {
    const f = mapRef.current.get(code);
    return !!(f?.is_menu && f?.is_enabled);
  }, []);

  const getScreenActions = useCallback((parentCode: string): FlatFeature[] => {
    const childCodes = parentChildRef.current.get(parentCode) ?? [];
    return childCodes
      .map(code => mapRef.current.get(code))
      .filter((f): f is FlatFeature => !!f && !f.is_menu);
  }, []);

  const getNavItems = useCallback((): FlatFeature[] => {
    return Array.from(mapRef.current.values()).filter(
      f => f.is_menu && !f.parent_id,
    );
  }, []);

  const getFeature = useCallback(
    (code: string) => mapRef.current.get(code),
    [],
  );

  return (
    <FeaturePermissionContext.Provider
      value={{
        isEnabled,
        isEditable,
        isMenuEnabled,
        getScreenActions,
        getNavItems,
        loadFeatures,
        clearFeatures,
        getFeature,
      }}
    >
      {children}
    </FeaturePermissionContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useFeaturePermission(): FeaturePermissionContextValue {
  const ctx = useContext(FeaturePermissionContext);
  if (!ctx) throw new Error('useFeaturePermission must be inside <FeaturePermissionProvider>');
  return ctx;
}